import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  CheckCircleIcon,
  ArrowRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import TransferAgentAutocomplete from '../trasferAgent/TransferAgentAutocomplete';
import CompanyLogo from '../../common/CompanyLogo';
import AuthenticatedNavigation from '../../common/AuthenticatedNavigation';
import { useAuth } from '../../../hooks/useAuth';
import { US_STATES } from '../../../utils/usStates';

interface QuestionnaireProps {
  facilityId: string | null;
  companyName?: string;
  companyInfo?: any;
  secInfo?: any;
  stockData?: any;
  onUpdateState: (updates: any) => void;
}

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  
  // Company Primary Contact (if not the user)
  companyContactName: string;
  companyContactTitle: string;
  companyContactEmail: string;
  companyContactPhone: string;
  
  // Company Information
  companyName: string;
  companyWebsite: string;
  companyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  
  // Business Information
  yearFounded: string;
  industry: string;
  businessDescription: string;
  
  // Share Information
  authorizedShares: string;
  outstandingShares: string;
  parValue: string;
  stateOfDomicile: string;
  
  // Transfer Agent Information
  transferAgentName: string;
  transferAgentId: string;
  transferAgentAddress: string;
  transferAgentPhone: string;
  
  // CFO Information
  cfoName: string;
  cfoEmail: string;
  
  // Legal Information
  legalCounselName: string;
  legalCounselFirm: string;
  legalCounselEmail: string;
  legalCounselPhone: string;
  
  // Account Setup
  password: string;
  confirmPassword: string;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ facilityId, companyName, companyInfo, secInfo, stockData, onUpdateState }) => {
  const navigate = useNavigate();
  const { selectionId } = useParams<{ selectionId: string }>();
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrimaryContact, setIsPrimaryContact] = useState<boolean | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [ccCFO, setCcCFO] = useState(false);
  const [ccLegalCounsel, setCcLegalCounsel] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmStepData, setConfirmStepData] = useState({
    step2: false, // Company Information confirmation
    step3: false, // Business Details confirmation
    step4: false  // Financial & Legal Contacts confirmation
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  

  // Helper function to convert state name to state code
  const getStateCode = (stateName: string): string => {
    if (!stateName) return '';
    
    // First check if it's already a state code (2 characters)
    if (stateName.length === 2) {
      const state = US_STATES.find(s => s.code === stateName.toUpperCase());
      return state ? state.code : '';
    }
    
    // Convert state name to state code
    const state = US_STATES.find(s => 
      s.name.toLowerCase() === stateName.toLowerCase()
    );
    
    return state ? state.code : '';
  };

  // Format phone number with dashes
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as XXX-XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Format number with commas
  const formatNumberWithCommas = (value: string) => {
    // Remove all non-digits
    const number = value.replace(/\D/g, '');
    // Add commas
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  // Track which fields were pre-populated - now as state
  const [prePopulatedFields, setPrePopulatedFields] = useState<Record<string, boolean>>({
    companyName: !!companyName,
    companyWebsite: false,
    companyAddress: false,
    city: false,
    state: false,
    zipCode: false,
    yearFounded: false,
    numberOfEmployees: false,
    industry: false,
    businessDescription: false,
    authorizedShares: false,
    outstandingShares: false,
    parValue: false,
    legalCounselName: false,
    legalCounselEmail: false,
    transferAgentName: false,
    stateOfDomicile: false
  });
  
  // Calculate step completion based on actual field values
  const calculateStepCompletion = (fields: Record<string, boolean>) => {
    const step2Fields = ['companyName', 'companyAddress', 'city', 'state', 'zipCode'];
    const step3Fields = ['authorizedShares', 'outstandingShares', 'parValue', 'stateOfDomicile', 'transferAgentName'];
    const step4Fields = ['legalCounselName'];
    
    const isStep2Complete = step2Fields.every(field => fields[field]);
    const isStep3Complete = step3Fields.every(field => fields[field]);
    const isStep4Complete = step4Fields.some(field => fields[field]); // At least legal counsel
    
    return { isStep2Complete, isStep3Complete, isStep4Complete };
  };
  
  // Initialize form data with company info from APIs
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phone: '',
    companyContactName: '',
    companyContactTitle: '',
    companyContactEmail: '',
    companyContactPhone: '',
    companyName: companyName || '',
    companyWebsite: '',
    companyAddress: '',
    city: '',
    state: '',
    zipCode: '',
    yearFounded: '',
    industry: '',
    businessDescription: '',
    authorizedShares: '',
    outstandingShares: '',
    parValue: '',
    stateOfDomicile: '',
    transferAgentName: '',
    transferAgentId: '',
    transferAgentAddress: '',
    transferAgentPhone: '',
    cfoName: '',
    cfoEmail: '',
    legalCounselName: '',
    legalCounselFirm: '',
    legalCounselEmail: '',
    legalCounselPhone: '',
    password: '',
    confirmPassword: ''
  });

  // Get facilityId from selectionId if not provided
  React.useEffect(() => {
    const getFacilityFromSelection = async () => {
      if (facilityId || !selectionId) return;
      
      try {
        console.log('🔍 Getting facilityId from selectionId:', selectionId);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/user-selections/${selectionId}`
        );
        
        if (response.data.success && response.data.data?.facilityId) {
          console.log('✅ Found facilityId:', response.data.data.facilityId);
          onUpdateState({ facilityId: response.data.data.facilityId });
        }
      } catch (error) {
        console.error('❌ Error getting facilityId from selection:', error);
      }
    };
    
    getFacilityFromSelection();
  }, [selectionId, facilityId, onUpdateState]);

  // Fetch pre-populated questionnaire data from backend
  React.useEffect(() => {
    const fetchQuestionnaireData = async () => {
      if (!facilityId) return;
      
      setIsLoadingData(true);
      const startTime = Date.now();
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/questionnaire/prepare/${facilityId}`
        );
        
        console.log('📊 Questionnaire API Response:', response.data);
        
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          
          console.log('📋 Pre-populated data received:');
          console.log('- Company Name:', data.companyName);
          console.log('- Company Address:', data.companyAddress);
          console.log('- City:', data.city);
          console.log('- State:', data.state, 'or StateOfIncorp:', data.stateOfIncorporation);
          console.log('- Zip Code:', data.zipCode);
          console.log('- Company Website:', data.companyWebsite);
          console.log('- Par Value:', data.parValue);
          console.log('- Authorized Shares:', data.authorizedShares);
          console.log('- Outstanding Shares:', data.sharesOutstanding);
          
          // Track which fields have data
          const fieldsWithData: Record<string, boolean> = {};
          
          // Update form data with pre-populated values from backend
          const newFormData: Partial<FormData> = {};
          
          // Company Information
          if (data.companyName?.value) {
            newFormData.companyName = data.companyName.value;
            fieldsWithData.companyName = true;
          }
          if (data.companyAddress?.value) {
            newFormData.companyAddress = data.companyAddress.value;
            fieldsWithData.companyAddress = true;
          }
          if (data.city?.value) {
            newFormData.city = data.city.value;
            fieldsWithData.city = true;
          }
          if (data.state?.value || data.stateOfIncorporation?.value) {
            const stateValue = data.state?.value || data.stateOfIncorporation?.value;
            console.log('🔍 Processing company address state:', stateValue);
            const stateCode = getStateCode(stateValue);
            console.log('🔍 Converted to state code:', stateCode);
            newFormData.state = stateCode;
            if (stateCode) {
              fieldsWithData.state = true;
            }
          }
          if (data.zipCode?.value) {
            newFormData.zipCode = data.zipCode.value;
            fieldsWithData.zipCode = true;
          }
          if (data.companyWebsite?.value) {
            newFormData.companyWebsite = data.companyWebsite.value;
            fieldsWithData.companyWebsite = true;
          }
          
          // Share Information
          if (data.authorizedShares?.value) {
            newFormData.authorizedShares = formatNumberWithCommas(data.authorizedShares.value.toString().replace(/,/g, ''));
            fieldsWithData.authorizedShares = true;
          }
          if (data.sharesOutstanding?.value) {
            newFormData.outstandingShares = formatNumberWithCommas(data.sharesOutstanding.value.toString().replace(/,/g, ''));
            fieldsWithData.outstandingShares = true;
          }
          if (data.parValue?.value) {
            newFormData.parValue = data.parValue.value;
            fieldsWithData.parValue = true;
          }
          
          // Fix: Convert state name to state code for state of domicile
          if (data.stateOfDomicile?.value) {
            console.log('🔍 Processing stateOfDomicile:', data.stateOfDomicile.value);
            const stateCode = getStateCode(data.stateOfDomicile.value);
            console.log('🔍 Converted to state code:', stateCode);
            newFormData.stateOfDomicile = stateCode;
            if (stateCode) {
              fieldsWithData.stateOfDomicile = true;
            }
          } else if (data.stateOfIncorporation?.value) {
            console.log('🔍 Processing stateOfIncorporation:', data.stateOfIncorporation.value);
            const stateCode = getStateCode(data.stateOfIncorporation.value);
            console.log('🔍 Converted to state code:', stateCode);
            newFormData.stateOfDomicile = stateCode;
            if (stateCode) {
              fieldsWithData.stateOfDomicile = true;
            }
          }
          
          // Business Information
          if (data.businessDescription?.value) {
            newFormData.businessDescription = data.businessDescription.value;
            fieldsWithData.businessDescription = true;
          }
          if (data.yearFounded?.value) {
            newFormData.yearFounded = data.yearFounded.value;
            fieldsWithData.yearFounded = true;
          }
          if (data.industry?.value) {
            newFormData.industry = data.industry.value;
            fieldsWithData.industry = true;
          }
          
          // Transfer Agent Information
          if (data.transferAgentName?.value) {
            newFormData.transferAgentName = data.transferAgentName.value;
            fieldsWithData.transferAgentName = true;
          }
          if (data.transferAgentAddress?.value) {
            newFormData.transferAgentAddress = data.transferAgentAddress.value;
          }
          if (data.transferAgentPhone?.value) {
            newFormData.transferAgentPhone = data.transferAgentPhone.value;
          }
          
          // Legal Information
          if (data.counselLawyerName?.value) {
            newFormData.legalCounselName = data.counselLawyerName.value;
            fieldsWithData.legalCounselName = true;
          }
          if (data.counselFirmName?.value) {
            newFormData.legalCounselFirm = data.counselFirmName.value;
          }
          if (data.counselEmail) {
            newFormData.legalCounselEmail = data.counselEmail;
            fieldsWithData.legalCounselEmail = true;
          }
          
          // Contact Information
          if (data.contactName?.value) {
            newFormData.companyContactName = data.contactName.value;
          }
          if (data.contactTitle?.value) {
            newFormData.companyContactTitle = data.contactTitle.value;
          }
          if (data.contactEmail) {
            newFormData.companyContactEmail = data.contactEmail;
          }
          if (data.contactPhone) {
            newFormData.companyContactPhone = data.contactPhone;
          }
          
          // Update form data
          setFormData(prev => ({ ...prev, ...newFormData }));
          
          // Update pre-populated fields tracker
          setPrePopulatedFields(prev => ({ ...prev, ...fieldsWithData }));
          
          // Calculate and set step completion
          const { isStep2Complete, isStep3Complete, isStep4Complete } = calculateStepCompletion(fieldsWithData);
          setConfirmStepData({
            step2: isStep2Complete,
            step3: isStep3Complete,
            step4: isStep4Complete
          });
        }
      } catch (error) {
        console.error('Error fetching questionnaire data:', error);
        // Don't show error to user since pre-population is optional
      } finally {
        // Ensure loading is shown for at least 1 second
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 1000 - elapsed);
        setTimeout(() => {
          setIsLoadingData(false);
        }, remaining);
      }
    };
    
    fetchQuestionnaireData();
  }, [facilityId]);

  const totalSteps = 5;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    // Apply formatting based on field name
    if (name === 'phone' || name === 'companyContactPhone') {
      formattedValue = formatPhoneNumber(value);
    } else if (name === 'authorizedShares' || name === 'outstandingShares') {
      formattedValue = formatNumberWithCommas(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  // Handler for transfer agent selection
  const handleTransferAgentSelect = (agent: any, name: string) => {
    if (agent) {
      setFormData(prev => ({
        ...prev,
        transferAgentName: agent.name,
        transferAgentId: agent._id,
        transferAgentAddress: `${agent.address.street}, ${agent.address.city}, ${agent.address.state} ${agent.address.zipCode}`,
        transferAgentPhone: agent.phone || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        transferAgentName: '',
        transferAgentId: '',
        transferAgentAddress: '',
        transferAgentPhone: ''
      }));
    }
  };
  
  // Helper function to get input props based on confirmation state
  const getFieldProps = (fieldName: string, step: number) => {
    const stepKey = `step${step}` as keyof typeof confirmStepData;
    const isDisabled = confirmStepData[stepKey] && prePopulatedFields[fieldName];
    
    return {
      disabled: isDisabled,
      className: `w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
        isDisabled ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'border-secondary-300'
      }`
    };
  };

  const handleNext = () => {
    // Validate primary contact selection on step 1
    if (currentStep === 1 && isPrimaryContact === null) {
      setShowValidationError(true);
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setShowValidationError(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Remove formatting from phone numbers and numeric values
  const cleanFormData = (data: FormData) => {
    return {
      ...data,
      phone: data.phone.replace(/\D/g, ''),
      companyContactPhone: data.companyContactPhone.replace(/\D/g, ''),
      legalCounselPhone: data.legalCounselPhone ? data.legalCounselPhone.replace(/\D/g, '') : '',
      transferAgentPhone: data.transferAgentPhone ? data.transferAgentPhone.replace(/\D/g, '') : '',
      authorizedShares: data.authorizedShares.replace(/\D/g, ''),
      outstandingShares: data.outstandingShares.replace(/\D/g, '')
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!facilityId) {
      alert('No facility ID found. Please go back and start the process again.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Clean the form data before submission
      const cleanedData = cleanFormData(formData);
      
      // First, get the questionnaire that should have been created
      const prepareResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/questionnaire/prepare/${facilityId}`
      );
      
      if (prepareResponse.data.success && prepareResponse.data.data._id) {
        // Prepare the form data to update the questionnaire
        const updateData = {
          contactEmail: cleanedData.email,
          personalInformation: {
            firstName: cleanedData.firstName,
            lastName: cleanedData.lastName,
            title: cleanedData.title,
            email: cleanedData.email,
            phone: cleanedData.phone
          },
          companyContact: isPrimaryContact ? null : {
            name: cleanedData.companyContactName,
            title: cleanedData.companyContactTitle,
            email: cleanedData.companyContactEmail,
            phone: cleanedData.companyContactPhone
          },
          companyInformation: {
            name: cleanedData.companyName,
            website: cleanedData.companyWebsite,
            address: cleanedData.companyAddress,
            city: cleanedData.city,
            state: cleanedData.state,
            zipCode: cleanedData.zipCode,
            yearFounded: cleanedData.yearFounded,
            industry: cleanedData.industry,
            businessDescription: cleanedData.businessDescription
          },
          shareInformation: {
            authorizedShares: cleanedData.authorizedShares,
            outstandingShares: cleanedData.outstandingShares,
            parValue: cleanedData.parValue,
            stateOfDomicile: cleanedData.stateOfDomicile
          },
          transferAgent: {
            name: cleanedData.transferAgentName,
            id: cleanedData.transferAgentId,
            address: cleanedData.transferAgentAddress,
            phone: cleanedData.transferAgentPhone
          },
          legalInformation: {
            counselName: cleanedData.legalCounselName,
            counselFirm: cleanedData.legalCounselFirm,
            counselEmail: cleanedData.legalCounselEmail,
            counselPhone: cleanedData.legalCounselPhone
          },
          additionalContacts: {
            ccCFO: ccCFO,
            cfoName: ccCFO ? cleanedData.cfoName : '',
            cfoEmail: ccCFO ? cleanedData.cfoEmail : '',
            ccLegalCounsel: ccLegalCounsel,
            legalCounselEmail: ccLegalCounsel ? cleanedData.legalCounselEmail : ''
          }
        };
        
        // Then submit it with the password
        const submitResponse = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/questionnaire/submit`,
          {
            questionnaireId: prepareResponse.data.data._id,
            formData: updateData,
            password
          }
        );
        
        if (submitResponse.data.success) {
          // Store the token if provided
          if (submitResponse.data.token) {
            localStorage.setItem('token', submitResponse.data.token);
            localStorage.setItem('user', JSON.stringify(submitResponse.data.user));
          }
          // Clear dashboard tour flag to ensure new users see the tour
          localStorage.removeItem('hasSeenDashboardTour');
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      console.error('Error submitting questionnaire:', error);
      alert(error.response?.data?.message || 'Failed to submit questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">Personal Information</h2>
            
            {/* Primary Contact Checkbox */}
            <div className={`mb-8 p-6 border rounded-xl ${showValidationError && isPrimaryContact === null ? 'bg-red-50 border-red-300' : 'bg-primary-50 border-primary-200'}`}>
              <div className="mb-4">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsPrimaryContact(true);
                      setShowValidationError(false);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isPrimaryContact === true 
                        ? 'bg-primary-950 text-white' 
                        : 'bg-white text-secondary-700 border border-secondary-300 hover:bg-secondary-50'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsPrimaryContact(false);
                      setShowValidationError(false);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isPrimaryContact === false 
                        ? 'bg-primary-950 text-white' 
                        : 'bg-white text-secondary-700 border border-secondary-300 hover:bg-secondary-50'
                    }`}
                  >
                    No
                  </button>
                  <span className="text-base font-medium text-secondary-900">
                    I am the Primary Contact and Authorized Signatory of {companyName || 'the company'}
                  </span>
                </div>
              </div>
              {showValidationError && isPrimaryContact === null && (
                <p className="text-sm text-red-600 mb-3">
                  Please select whether you are the primary contact and authorized signatory
                </p>
              )}
              {isPrimaryContact === false && (
                <div className="pt-3 border-t border-primary-200">
                  <p className="text-sm text-secondary-600">
                    The primary contact and authorized signatory will be invited to the Company Dashboard with ability to monitor and manage the equity facility, and will sign all facility documents.
                  </p>
                </div>
              )}
            </div>

            {/* Personal Information Fields */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., CEO, CFO, VP Finance"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="555-123-4567"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
            </div>

            {/* Company Primary Contact Fields (shown when not primary contact) */}
            {isPrimaryContact === false && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                className="border-t border-secondary-200 pt-6"
              >
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Company Primary Contact and Authorized Signatory</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="companyContactName"
                      value={formData.companyContactName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required={isPrimaryContact === false}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="companyContactTitle"
                      value={formData.companyContactTitle}
                      onChange={handleInputChange}
                      placeholder="e.g., CEO, President"
                      className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required={isPrimaryContact === false}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="companyContactEmail"
                      value={formData.companyContactEmail}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required={isPrimaryContact === false}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="companyContactPhone"
                      value={formData.companyContactPhone}
                      onChange={handleInputChange}
                      placeholder="555-123-4567"
                      className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required={isPrimaryContact === false}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="flex items-center gap-4 mb-6">
              {stockData?.ticker && (
                <CompanyLogo 
                  ticker={stockData.ticker} 
                  companyName={formData.companyName || companyName} 
                  size="lg" 
                />
              )}
              <h2 className="text-2xl font-bold text-secondary-900">Company Information</h2>
            </div>
            
            {/* Confirmation checkbox if data is pre-populated */}
            {(prePopulatedFields.companyName || prePopulatedFields.companyWebsite || 
              prePopulatedFields.companyAddress || prePopulatedFields.city || 
              prePopulatedFields.state || prePopulatedFields.zipCode) && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmStep2"
                    checked={confirmStepData.step2}
                    onChange={(e) => setConfirmStepData({...confirmStepData, step2: e.target.checked})}
                    className="mt-1 h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="confirmStep2" className="text-sm font-medium text-secondary-900">
                    I confirm the pre-populated information is correct
                  </label>
                </div>
                {confirmStepData.step2 && (
                  <p className="mt-2 ml-7 text-xs text-secondary-600">
                    Uncheck to edit the fields below
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    {...getFieldProps('companyName', 2)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Company Website
                  </label>
                  <input
                    type="url"
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    {...getFieldProps('companyWebsite', 2)}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Company Address *
                </label>
                <input
                  type="text"
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleInputChange}
                  {...getFieldProps('companyAddress', 2)}
                  required
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    {...getFieldProps('city', 2)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    State *
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    {...getFieldProps('state', 2)}
                    required
                  >
                    <option value="">Select State</option>
                    {US_STATES.map(state => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    {...getFieldProps('zipCode', 2)}
                    required
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">Business Details</h2>
            
            {/* Confirmation checkbox if data is pre-populated */}
            {(prePopulatedFields.yearFounded || prePopulatedFields.industry || 
              prePopulatedFields.businessDescription) && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmStep3"
                    checked={confirmStepData.step3}
                    onChange={(e) => setConfirmStepData({...confirmStepData, step3: e.target.checked})}
                    className="mt-1 h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="confirmStep3" className="text-sm font-medium text-secondary-900">
                    I confirm the pre-populated information is correct
                  </label>
                </div>
                {confirmStepData.step3 && (
                  <p className="mt-2 ml-7 text-xs text-secondary-600">
                    Uncheck to edit the fields below
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Year Founded *
                  </label>
                  <input
                    type="text"
                    name="yearFounded"
                    value={formData.yearFounded}
                    onChange={handleInputChange}
                    placeholder="2020"
                    {...getFieldProps('yearFounded', 3)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Industry *
                  </label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="Technology"
                    {...getFieldProps('industry', 3)}
                    required
                  />
                </div>
              </div>
              
              {/* Share Information - Always requires user confirmation */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Share Information</h3>
                <p className="text-sm text-amber-700 mb-4">
                  Please confirm the following share information is accurate:
                </p>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Authorized Shares *
                      </label>
                      <input
                        type="text"
                        name="authorizedShares"
                        value={formData.authorizedShares}
                        onChange={handleInputChange}
                        placeholder="e.g., 100,000,000"
                        {...getFieldProps('authorizedShares', 3)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Outstanding Shares *
                      </label>
                      <input
                        type="text"
                        name="outstandingShares"
                        value={formData.outstandingShares}
                        onChange={handleInputChange}
                        placeholder="e.g., 50,000,000"
                        {...getFieldProps('outstandingShares', 3)}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Par Value per Share *
                    </label>
                    <input
                      type="text"
                      name="parValue"
                      value={formData.parValue}
                      onChange={handleInputChange}
                      placeholder="e.g., $0.001"
                      {...getFieldProps('parValue', 3)}
                      required
                    />
                    {formData.parValue && secInfo?.parValue && formData.parValue !== secInfo.parValue && (
                      <p className="mt-2 text-sm text-amber-600">
                        Note: SEC records show par value as {secInfo.parValue}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* State of Domicile */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  State of Domicile *
                </label>
                <select
                  name="stateOfDomicile"
                  value={formData.stateOfDomicile}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {secInfo?.state_of_incorporation && (
                  <p className="mt-2 text-sm text-secondary-600">
                    Note: SEC records show state of incorporation as {secInfo.state_of_incorporation}
                  </p>
                )}
              </div>
              
              {/* Transfer Agent Information */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-8">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Transfer Agent Information</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Transfer Agent *
                    </label>
                    <TransferAgentAutocomplete
                      value={formData.transferAgentName}
                      onChange={handleTransferAgentSelect}
                      required
                    />
                  </div>
                  
                  {formData.transferAgentAddress && (
                    <div className="mt-4 p-4 bg-white rounded-lg">
                      <p className="text-sm text-secondary-600">
                        <span className="font-medium">Address:</span> {formData.transferAgentAddress}
                      </p>
                      {formData.transferAgentPhone && (
                        <p className="text-sm text-secondary-600 mt-1">
                          <span className="font-medium">Phone:</span> {formData.transferAgentPhone}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Business Description *
                </label>
                <textarea
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Please provide a brief description of your business and its primary activities..."
                  {...getFieldProps('businessDescription', 3)}
                  required
                />
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">Financial & Legal Contacts</h2>
            
            {/* Confirmation checkbox if data is pre-populated */}
            {(prePopulatedFields.legalCounselName || prePopulatedFields.legalCounselEmail) && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmStep4"
                    checked={confirmStepData.step4}
                    onChange={(e) => setConfirmStepData({...confirmStepData, step4: e.target.checked})}
                    className="mt-1 h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="confirmStep4" className="text-sm font-medium text-secondary-900">
                    I confirm the pre-populated information is correct
                  </label>
                </div>
                {confirmStepData.step4 && (
                  <p className="mt-2 ml-7 text-xs text-secondary-600">
                    Uncheck to edit the fields below
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-6">
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <p className="text-sm text-primary-700">
                    Select which contacts should be CC'd on facility communications. 
                    You can choose to include your CFO and/or legal counsel on all notices.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Chief Financial Officer</h3>
                <div className="mb-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={ccCFO}
                      onChange={(e) => setCcCFO(e.target.checked)}
                      className="h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-secondary-900">
                      CC CFO on all facility notices and communications
                    </span>
                  </label>
                </div>
                {ccCFO && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="grid md:grid-cols-2 gap-6"
                  >
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        CFO Name *
                      </label>
                      <input
                        type="text"
                        name="cfoName"
                        value={formData.cfoName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required={ccCFO}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        CFO Email *
                      </label>
                      <input
                        type="email"
                        name="cfoEmail"
                        value={formData.cfoEmail}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required={ccCFO}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Legal Counsel</h3>
                <div className="mb-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={ccLegalCounsel}
                      onChange={(e) => setCcLegalCounsel(e.target.checked)}
                      className="h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-secondary-900">
                      CC Legal Counsel on all facility notices and communications
                    </span>
                  </label>
                </div>
                {ccLegalCounsel && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="grid md:grid-cols-2 gap-6"
                  >
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Legal Counsel Name/Firm *
                      </label>
                      <input
                        type="text"
                        name="legalCounselName"
                        value={formData.legalCounselName}
                        onChange={handleInputChange}
                        {...(ccLegalCounsel ? {} : getFieldProps('legalCounselName', 4))}
                        required={ccLegalCounsel}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Legal Counsel Email *
                      </label>
                      <input
                        type="email"
                        name="legalCounselEmail"
                        value={formData.legalCounselEmail}
                        onChange={handleInputChange}
                        {...(ccLegalCounsel ? {} : getFieldProps('legalCounselEmail', 4))}
                        required={ccLegalCounsel}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">Create Your Account</h2>
            
            <div className="space-y-6">
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <p className="text-sm text-primary-700">
                    Create a password to secure your Company Dashboard access.
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a strong password"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  minLength={8}
                />
                <p className="mt-2 text-sm text-secondary-600">
                  Must be at least 8 characters long
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  minLength={8}
                />
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    Passwords do not match
                  </p>
                )}
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Account Summary</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-secondary-600">Email:</dt>
                    <dd className="text-sm font-medium text-secondary-900">{formData.email}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-secondary-600">Company:</dt>
                    <dd className="text-sm font-medium text-secondary-900">{formData.companyName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-secondary-600">Your Role:</dt>
                    <dd className="text-sm font-medium text-secondary-900">{formData.title}</dd>
                  </div>
                  {ccCFO && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-secondary-600">CFO CC'd:</dt>
                      <dd className="text-sm font-medium text-secondary-900">{formData.cfoEmail}</dd>
                    </div>
                  )}
                  {ccLegalCounsel && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-secondary-600">Legal CC'd:</dt>
                      <dd className="text-sm font-medium text-secondary-900">{formData.legalCounselEmail}</dd>
                    </div>
                  )}
                </dl>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-700">
                  By creating an account, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-financial">
      {/* Simple Navigation Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Home
            </button>
            <img src="/24lp-logo-primary.png" alt="Twenty Four Capital" className="h-20 md:h-24 w-auto object-contain block" />
          </div>
        </div>
      </header>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Loading Overlay */}
        {isLoadingData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-8 shadow-xl">
              <div className="flex items-center space-x-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-950"></div>
                <p className="text-lg font-medium text-secondary-900">Loading your information...</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="text-center mb-12">
          {stockData?.ticker && (
            <div className="flex justify-center mb-6">
              <CompanyLogo 
                ticker={stockData.ticker} 
                companyName={companyName} 
                size="xl" 
              />
            </div>
          )}
          <h1 className="text-4xl font-bold text-secondary-900 mb-4">
            Complete Your Application
          </h1>
          <p className="text-xl text-secondary-600">
            Just a few more details to finalize your facility
          </p>
          {companyName && (
            <p className="text-lg text-secondary-700 mt-2">
              for <span className="font-semibold">{companyName}</span>
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-secondary-600">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-secondary-600">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-secondary-200 rounded-full h-2">
            <div 
              className="bg-primary-950 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <motion.div 
          key={currentStep}
          className="card-elevated mb-8"
        >
          {renderStepContent()}
        </motion.div>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              className="btn-primary flex items-center"
            >
              Next
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Complete Application'}
              <CheckCircleIcon className="h-5 w-5 ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire; 